import { mergeRerankedScores } from '@/layers/business-logic/programs/rerank-merge';
import {
  fetchRerankScores,
  RerankCandidate,
  RerankPatientParams,
  RerankScore,
} from '@/layers/presentation/services/rerank';
import logger from '@/layers/support/monitoring/logger';
import { Program } from '@/types/models';
import { useEffect, useMemo, useRef, useState } from 'react';

const LOG_MODULE = 'FE [Presentation / Hooks / useProgressiveRerank]';

const MAX_SNIPPET_CHARS = 512;

export interface UseProgressiveRerankArgs {
  /** EA results in DB order. */
  ea: Program[];
  /** Non-EA results in DB order. */
  nonEa: Program[];
  /** The settled search query. */
  query: string;
  /**
   * A signature of the settled query + filters that produced these results.
   * Used as the race guard: a rerank response is only applied if its signature
   * still matches the current one (rapid re-queries drop stale responses), and
   * rerank fires exactly once per distinct signature.
   */
  signature: string;
  /**
   * The signature whose result groups have ALL finished loading (EA + non-EA
   * both settled). The rerank fires only once this matches `signature`, so it
   * never fires over a partial candidate set while one group is still loading,
   * and `isRefining` stays false until every group is rendered. Omit (or pass
   * `undefined`) to treat the current signature as already-settled — preserving
   * the prior behavior for callers that don't load groups separately.
   */
  settledSignature?: string | null;
  /** Whether rerank should run at all (e.g. a real text query is present). */
  enabled: boolean;
  /** Optional patient context forwarded to the reranker. */
  patientParams?: RerankPatientParams;
}

export interface UseProgressiveRerankResult {
  ea: Program[];
  nonEa: Program[];
  /** True while a rerank request is in flight ("refining results…" affordance). */
  isRefining: boolean;
}

function toCandidate(p: Program): RerankCandidate {
  // Mirrors the candidate shape the previous inline server-side rerank built.
  const rawConditions = p.details?.conditions;
  const conditions = Array.isArray(rawConditions)
    ? String(rawConditions.slice(0, 20).join(' | '))
    : undefined;

  const snippet = (p.lead || p.overview || '').slice(0, MAX_SNIPPET_CHARS);

  return { id: p.id, title: p.name, snippet, conditions };
}

/**
 * Progressive rerank: returns the DB-ordered groups immediately, then fires a
 * single async rerank over the union of ea+nonEa and re-orders each group in
 * place when scores arrive. The EA / non-EA grouping is preserved — only
 * intra-group order changes.
 *
 * Failure / timeout / empty scores keep DB order silently. Rapid re-queries are
 * handled by the `signature` race guard: stale responses are dropped, and
 * rerank fires at most once per settled signature.
 */
export function useProgressiveRerank({
  ea,
  nonEa,
  query,
  signature,
  settledSignature,
  enabled,
  patientParams,
}: UseProgressiveRerankArgs): UseProgressiveRerankResult {
  const [scoresBySignature, setScoresBySignature] = useState<{
    signature: string;
    scores: RerankScore[];
  } | null>(null);
  const [isRefining, setIsRefining] = useState(false);

  // Monotonic id of the most recently fired request. A response is only honored
  // if it belongs to the latest request, so a stale in-flight response from a
  // prior query is dropped (race guard).
  const latestRequestIdRef = useRef(0);
  // The signature we have already fired a rerank for. Guarantees exactly one
  // request per settled query+filters, even across the multiple renders that
  // happen as the async DB results populate under a stable signature.
  const firedSignatureRef = useRef<string | null>(null);

  // Latest inputs, read at fire time, so an unrelated re-render that passes a
  // fresh `ea`/`nonEa`/params array reference does not by itself re-fire.
  const inputsRef = useRef({ ea, nonEa, query, patientParams });
  inputsRef.current = { ea, nonEa, query, patientParams };

  const hasResults = ea.length > 0 || nonEa.length > 0;

  // Both result groups have finished loading for the CURRENT signature. When
  // the caller omits `settledSignature` (undefined), the current signature is
  // treated as already-settled (prior behavior). A `null` value means no
  // signature has fully settled yet, so we hold off firing.
  const groupsSettled = settledSignature === undefined || settledSignature === signature;

  useEffect(() => {
    const { query: curQuery, patientParams: curParams } = inputsRef.current;
    const curEa = inputsRef.current.ea;
    const curNonEa = inputsRef.current.nonEa;

    if (!enabled || !curQuery.trim() || !hasResults) {
      return;
    }
    // Wait until BOTH result groups have settled for this signature before
    // firing. EA and non-EA load as separate requests; firing on the first to
    // arrive would rerank a partial set and flash "refining…" over the other
    // group's still-visible spinner. Gating here keeps the rerank over the full
    // ea+nonEa union and the "refining" affordance hidden until all groups render.
    if (!groupsSettled) {
      return;
    }
    // Fire at most once per settled signature; results may arrive after the
    // signature settles (async fetch), so we wait for hasResults before firing.
    if (firedSignatureRef.current === signature) {
      return;
    }
    firedSignatureRef.current = signature;

    const requestId = ++latestRequestIdRef.current;
    const candidates = [...curEa, ...curNonEa].map(toCandidate);

    logger.debug(`${LOG_MODULE} firing rerank`, {
      signature,
      requestId,
      candidateCount: candidates.length,
    });
    setIsRefining(true);

    fetchRerankScores({ query: curQuery.trim(), candidates, patientParams: curParams })
      .then(scores => {
        // Race guard: ignore responses superseded by a newer request.
        if (requestId !== latestRequestIdRef.current) {
          logger.debug(`${LOG_MODULE} dropping stale rerank response`, {
            requestId,
            latestRequestId: latestRequestIdRef.current,
          });
          return;
        }
        if (scores.length > 0) {
          setScoresBySignature({ signature, scores });
        }
      })
      .finally(() => {
        if (requestId !== latestRequestIdRef.current) return;
        setIsRefining(false);
      });
    // Keyed on signature/enabled/hasResults/groupsSettled: fires once per
    // settled query+filters as soon as results exist AND both groups have
    // settled. Array contents are read from a ref so unstable references don't
    // re-trigger; the firedSignatureRef dedupes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled, hasResults, groupsSettled]);

  // Only apply scores whose signature still matches the current results.
  const activeScores = useMemo(
    () =>
      scoresBySignature && scoresBySignature.signature === signature
        ? scoresBySignature.scores
        : [],
    [scoresBySignature, signature],
  );

  const orderedEa = useMemo(() => mergeRerankedScores(ea, activeScores), [ea, activeScores]);
  const orderedNonEa = useMemo(
    () => mergeRerankedScores(nonEa, activeScores),
    [nonEa, activeScores],
  );

  return { ea: orderedEa, nonEa: orderedNonEa, isRefining };
}
